
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { FormControl, Select, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import BaseCard from "../../../components/Cards/Card";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import crossmap from "../../../assets/crossmap.svg";
import {
  GoogleMap,
  Marker,
  Circle,
  useLoadScript
} from "@react-google-maps/api";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";
import LocationPermissionModal from "../../AdminPages/ClientAcquisition/InvitedClients/permissionModal"; // ✅ reuse same modal
import { showToast } from "../../../Utlis/messageHandler";
import { Country, State, City } from "country-state-city";
import SearchableDropdown from "../../../components/SearchComponent";
import { MAPS_API_KEY } from "../../../components/mapKey";
import useGeolocationPermission from "../../../Utlis/useGeolocationPermission";

// ---------- helpers ----------
const defaultCenter = { lat: 33.6844, lng: 73.0479 };
const circularRadius = 100;

const LocatingPill = ({ label, zIndex = 10 }) => (
  <Box
    sx={{
      position: "absolute",
      top: 12,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 0.75,
      backgroundColor: "#fff",
      borderRadius: 5,
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      fontSize: 13,
      fontFamily: "Poppins, sans-serif",
    }}
  >
    <CircularProgress size={16} />
    {label}
  </Box>
);



// Build a simple street address
const buildStreetAddress = (addr = {}) => {
  const house = addr.house_number;
  const road =
    addr.road ||
    addr.residential ||
    addr.pedestrian ||
    addr.footway ||
    addr.path ||
    addr.cycleway;

  if (house && road) return `${house} ${road}`;
  if (road) return road;

  return (
    addr.neighbourhood ||
    addr.suburb ||
    addr.hamlet ||
    addr.quarter ||
    addr.village ||
    addr.town ||
    addr.city_district ||
    ""
  );
};

// Map ISO2 → Region/Continent
const iso2ToRegion = (iso2) => {
  const m = (iso2 || "").toUpperCase();
  const NA = ["US", "CA", "MX"];
  const SA = ["BR", "AR", "CL", "CO", "PE", "UY", "PY", "BO", "VE", "EC"];
  const EU = ["GB", "FR", "DE", "ES", "IT", "NL", "SE", "NO", "FI", "PL", "PT", "IE", "AT", "GR", "CZ"];
  const AS = ["PK", "IN", "CN", "JP", "AE", "SA", "SG", "TH", "ID", "MY", "BD", "PH", "KR"];
  const AF = ["ZA", "NG", "EG", "KE", "MA", "TZ", "UG", "DZ"];
  const OC = ["AU", "NZ", "FJ", "PG"];

  if (NA.includes(m)) return "North America";
  if (SA.includes(m)) return "South America";
  if (EU.includes(m)) return "Europe";
  if (AS.includes(m)) return "Asia";
  if (AF.includes(m)) return "Africa";
  if (OC.includes(m)) return "Oceania";
  return "Unknown";
};

const Location = () => {
  const [position, setPosition] = useState(defaultCenter);


  const { isLoaded } = useLoadScript({
  googleMapsApiKey: MAPS_API_KEY,
});

  const { t } = useTranslation();
  const isOnline = () => navigator.onLine;

  // Address states
  const [displayAddress, setDisplayAddress] = useState(t("Fetching address..."));
  const [streetAddress, setStreetAddress] = useState("");
  const addressRef = useRef(null);
  const locationRequestRef = useRef(0);
  const [locating, setLocating] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState("");
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [selectedStateIso, setSelectedStateIso] = useState("");
  // UI states
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFullMap, setShowFullMap] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false); // ✅ new state
  // Cascading dropdown state
  const [countriesData, setCountriesData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
const [manualAddress, setManualAddress] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [isContinueClicked, setIsContinueClicked] = useState(false);
  // token
  const profileUpdateToken =
    useSelector((s) => s.auth?.profileUpdateToken) ||
    localStorage.getItem("profileUpdateToken");
  const rawEncryptedToken = localStorage.getItem("token");
  const fallbackToken = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;
  const authHeader = profileUpdateToken || fallbackToken;

  // structured fields
  const [locationFields, setLocationFields] = useState({
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    city: "",
    province: "",
    postalCode: "",
    country: "",
    countryCode: "",
    region: "",
    continent: "",
    timezone: "",
  });

  // Extract structured info
  const extractAddressFromNominatim = (addr) => {
    if (!addr) return {};
    const iso2 = (addr.country_code || "").toUpperCase();

    return {
      city:
        addr.city ||
        addr.town ||
        addr.village ||
        addr.municipality ||
        addr.city_district ||
        "",
      province: addr.state || "",
      postalCode: addr.postcode || "",
      country: addr.country || "",
      countryCode: iso2,
      region: iso2ToRegion(iso2),
      continent: iso2ToRegion(iso2),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    };
  };

  // Reverse geocode
  const fetchAddress = async ({ lat, lng }) => {
    if (!isOnline()) {
      setLocating(false);
      showToast(toast.error, {
        message_en: t("locationInternetError") || "No internet connection",
        message_es: t("locationInternetError") || "Sin conexión a internet"
      });
      return;
    }

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        // { headers: { "User-Agent": "yourapp/1.0 (contact@example.com)" } }
      );
      const data = await res.json();

      const formatted = data?.display_name || "Address not found";
      setResolvedAddress(data?.display_name || "");
      setDisplayAddress(formatted);
      if (addressRef.current) addressRef.current.value = formatted;

      const simple = buildStreetAddress(data?.address || {});
      setStreetAddress(simple);

      const extracted = extractAddressFromNominatim(data?.address);

      setLocationFields({
        ...extracted,
        latitude: lat,
        longitude: lng,
      });

      // ✅ Update dropdown fields to match the new location
      if (!manualAddress && extracted.country) {
        setSelectedCountry(extracted.country);
        
        // Find country ISO code
        const countryObj = countriesData.find(c => c.name === extracted.country);
        if (countryObj) {
          setSelectedCountryIso(countryObj.isoCode);
          
          // Get states for this country
          const stateList = State.getStatesOfCountry(countryObj.isoCode)
            .map(s => s.name)
            .sort();
          setStates(stateList);
          
          // Update selected state if available
          if (extracted.province) {
            setSelectedState(extracted.province);
            
            // Get cities for this state
            const stateObj = State.getStatesOfCountry(countryObj.isoCode).find(
              s => s.name === extracted.province
            );
            if (stateObj) {
              setSelectedStateIso(stateObj.isoCode);
              const cityList = City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode)
                .map(c => c.name)
                .sort();
              setCities(cityList);
              
              // Update selected city if available
              if (extracted.city) {
                setSelectedCity(extracted.city);
              } else {
                setSelectedCity("");
              }
            }
          } else {
            setSelectedState("");
            setSelectedCity("");
            setCities([]);
          }
        }
      }
    } catch {
      setDisplayAddress("Address not found");
      setResolvedAddress("");
      setStreetAddress("");
      if (addressRef.current) addressRef.current.value = "Address not found";
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    setLoadingCountries(true);
    try {
      const list = Country.getAllCountries().map((c) => ({
        name: c.name,
        isoCode: c.isoCode,
      }));

      setCountriesData(list);
      setCountries(list.map((c) => c.name).sort());
    } catch (err) {
      showToast(toast.error, {
        message_en: t("alerts.failedToLoadCountries"),
        message_es: t("alerts.failedToLoadCountries"),
      });
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  // When user picks a country → populate states (don't auto-fill address in manual mode)
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setSelectedState("");
    setSelectedCity("");

    setStates([]);
    setCities([]);

    const obj = countriesData.find((c) => c.name === value);

    if (obj) {
      setSelectedCountryIso(obj.isoCode);

      const stateList = State.getStatesOfCountry(obj.isoCode).map((s) => ({
        name: s.name,
        isoCode: s.isoCode,
      }));

      setStates(stateList.map((s) => s.name).sort());
    }

    setLocationFields((prev) => ({ ...prev, country: value }));
  };


  // When user picks a state → fetch cities (don't auto-fill address in manual mode)
  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedCity("");

    setCities([]);

    setLocationFields((prev) => ({ ...prev, province: value }));

    const obj = State.getStatesOfCountry(selectedCountryIso).find(
      (s) => s.name === value
    );

    if (obj) {
      setSelectedStateIso(obj.isoCode);

      const cityList = City.getCitiesOfState(selectedCountryIso, obj.isoCode)
        .map((c) => c.name)
        .sort();

      setCities(cityList);
    }
  };

  // Geocode helper for selected city/state/country
  const geocodeSelection = async (country, state, city) => {
    try {
      const q = [city, state, country].filter(Boolean).join(", ");
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`,
        // { headers: { "User-Agent": "yourapp/1.0 (contact@example.com)" } }
      );
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        updatePosition(coords);
      } else {
        showToast(toast.error, {
          message_en: t("alerts.locationNotFoundForSelection"),
          message_es: t("alerts.locationNotFoundForSelection")
        });
      }
    } catch {
      showToast(toast.error, {
        message_en: t("alerts.errorLocatingSelection"),
        message_es: t("alerts.errorLocatingSelection")
      });
    }
  };

  // When user picks a city → update map
  const handleCityChange = async (value) => {
    setSelectedCity(value);
    setLocationFields((prev) => ({ ...prev, city: value }));
    await geocodeSelection(selectedCountry, selectedState, value);
  };

  // Search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showToast(toast.error, {
        message_en: t("alerts.enterLocation"),
        message_es: t("alerts.enterLocation")
      });
      return;
    }

    if (!isOnline()) {
      showToast(toast.error, {
        message_en: t("locationInternetError") || "No internet connection",
        message_es: t("locationInternetError") || "Sin conexión a internet"
      });
      return;
    }
    let data = null;
    try {
      let res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`);
      data = await res.json();
      if (!Array.isArray(data) || data.length === 0) throw new Error("No results");
    } catch {
      try {
        let res = await fetch(`https://corsproxy.io/?https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`);
        data = await res.json();
      } catch {
        data = null;
      }
    }
    if (Array.isArray(data) && data.length > 0) {
      const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      updatePosition(coords);
    } else {
      showToast(toast.error, {
        message_en: t("alerts.locationNotFound"),
        message_es: t("alerts.locationNotFound")
      });
    }
  };


  const updatePosition = (coords) => {
    setLocationConfirmed(true);
    setPosition(coords);
    fetchAddress(coords);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      showToast(toast.error, {
        message_en: t("alerts.geolocationNotSupported"),
        message_es: t("alerts.geolocationNotSupported")
      });
      return;
    }

    if (!isOnline()) {
      showToast(toast.error, {
        message_en: t("locationInternetError") || "No internet connection. Please check your connection.",
        message_es: t("locationInternetError") || "Sin conexión a internet. Por favor verifica tu conexión."
      });
      return;
    }

    const requestId = ++locationRequestRef.current;
    const isStale = () => requestId !== locationRequestRef.current;

    setLocating(true);

    const onSuccess = (pos) => {
      if (isStale()) return;

      setShowPermissionModal(false);
      updatePosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    };

    const onFinalError = (error) => {
      if (isStale()) return;

      setLocating(false);

      if (error.code === error.PERMISSION_DENIED) {
        setShowPermissionModal(true);
        return;
      }

      setDisplayAddress(t("unableToFetchLocation"));
      setResolvedAddress("");
      setStreetAddress("");
      showToast(toast.error, {
        message_en: t("unableToFetchLocationToast"),
        message_es: t("unableToFetchLocationToast")
      });
    };

    // Attempt 1: fast network/Wi-Fi fix, cached position up to 1 min old
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (error) => {
        if (isStale()) return;

        if (error.code === error.PERMISSION_DENIED) {
          onFinalError(error);
          return;
        }

        // Attempt 2: GPS, can be slow (especially on iOS)
        navigator.geolocation.getCurrentPosition(onSuccess, onFinalError, {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0,
        });
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  };

  // Trigger geolocation after countries data is loaded
  useEffect(() => {
    if (countriesData.length > 0) {
      getCurrentLocation();
    }
  }, [countriesData.length]);

  // Reflect the browser's location setting changing while the page is open
  useGeolocationPermission({
    retryOnFocus: showPermissionModal,
    onGranted: () => {
      if (manualAddress || locating || countriesData.length === 0) return;
      if (showPermissionModal || !locationConfirmed) getCurrentLocation();
    },
    onDenied: () => {
      if (!manualAddress) setShowPermissionModal(true);
    },
  });

  // Monitor internet connectivity
  useEffect(() => {
    const handleOnline = () => {
      showToast(toast.success, {
        message_en: t("internetRestored") || "Internet connection restored",
        message_es: t("internetRestored") || "Conexión a internet restaurada"
      });
      // Retry locating (manual mode keeps the user's selection)
      if (!manualAddress) {
        getCurrentLocation();
      } else if (position?.lat && position?.lng) {
        fetchAddress(position);
      }
    };

    const handleOffline = () => {
      showToast(toast.error, {
        message_en: t("internetConnectionLost") || "Internet connection lost",
        message_es: t("internetConnectionLost") || "Conexión a internet perdida"
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [position, manualAddress]);

  // Drag marker
  const handleMarkerDragEnd = (e) => {
    const ll = e.target.getLatLng();
    const coords = { lat: ll.lat, lng: ll.lng };
    updatePosition(coords);
  };

  // Save to API
  const saveLocation = async () => {
    try {
      setSaving(true);
      if (!authHeader) {
        showToast(toast.error, {
          message_en: t("alerts.notAuthenticated"),
          message_es: t("alerts.notAuthenticated")
        }); return false;
      }

      if (!locationConfirmed || !(streetAddress || resolvedAddress)) {
        showToast(toast.error, {
          message_en: t("locationRequiredToContinue"),
          message_es: t("locationRequiredToContinue")
        });
        return false;
      }

      const payload = {
        ...locationFields,
        address: streetAddress || resolvedAddress,
        latitude: position.lat,
        longitude: position.lng,
      };

      const res = await fetch(`${BASE_URL}/account-executive/profile/location`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authHeader}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to update location");
console.log("Location update response status:", res);
      return true;
    } catch (err) {
      showToast(toast.error, {
        message_en: t("alerts.errorSavingLocation"),
        message_es: t("alerts.errorSavingLocation")
      }); return false;
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    const ok = await saveLocation();
    if (ok) navigate("/registration-verification");
  };

  return (
    <Box sx={{ py: isMobile ? 5 : 8, px: 2, mt: isMobile ? 2 : 0 }}>
      <BaseCard maxWidth={isMobile ? "100%" : "70%"}>


        <CustomText customStyle={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
          {t("pinLocationTitle")}
        </CustomText>
        <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, pt: 0.7 }}>
          {t("pinLocationSubtitle")}
        </CustomText>


<Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 ,mb:2, }}>
  <input
    type="checkbox"
    id="manualAddress"
    checked={manualAddress}
    onChange={(e) => setManualAddress(e.target.checked)}
  />
  <label htmlFor="manualAddress" style={{ fontSize: 14 }}>
    Manual address
  </label>
</Box>

        {/* Search input */}
        {searchBarOpen && (
          <Box sx={{ mb: 2 }}>
            <input
              type="text"
              placeholder={t("searchLocation")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                console.log("⌨️ Key pressed:", e.key);

                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();

                  console.log("✅ ENTER detected — triggering handleSearch()");
                  handleSearch();
                }
              }}
              style={{
                width: "100%",
                padding: "10px 14px",
                fontSize: "15px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </Box>
        )}

        {/* Map preview */}
        {!manualAddress && (
          <>
        <Box sx={{ position: "relative", height: 300, borderRadius: 2, overflow: "hidden", mb: 2 }}>
      <Box sx={{ position: "relative", height: 300, borderRadius: 2, overflow: "hidden", mb: 2 }}>
  {locating && <LocatingPill label={t("fetchingLocation")} />}
  {isLoaded && (
    <GoogleMap
      center={position}
      zoom={15}
      mapContainerStyle={{ width: "100%", height: "100%" }}
    >
      <Marker
        position={position}
        draggable
        onDragEnd={(e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          const coords = { lat, lng };
          updatePosition(coords);
        }}
      />

      <Circle
        center={position}
        radius={circularRadius}
        options={{
          fillColor: "#3f51b5",
          fillOpacity: 0.2,
          strokeColor: "#3f51b5",
          strokeOpacity: 0.5,
        }}
      />
    </GoogleMap>
  )}
</Box>
        </Box>


        <textarea
          ref={addressRef}
          type="text"
          readOnly
          value={displayAddress}
          placeholder="Address will be detected from map"
          style={{
            width: "100%",
            padding: "12px",
            fontSize: "16px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            marginBottom: "20px",
            backgroundColor: "#f4f4f4",
            fontFamily: "Poppins, sans-serif",
            resize: "vertical",
            overflowY: "auto",
            lineHeight: "1.4",
          }}
        />
        </>
        )}
        {/* Location Fields - Always Visible */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>

          <Box>
            <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, textAlign: "left" }}>
              {t("country")} <span style={{ color: "red" }}>*</span>
            </CustomText>
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  borderRadius: "6px",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#091E4224",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  color: "#6B778C",
                  "&.Mui-focused": {
                    color: "#1976d2",
                  },
                },
              }}
            >

              <SearchableDropdown
                value={selectedCountry}
                onChange={handleCountryChange}
                options={countries}
                placeholder={t("selectCountry")}
                // disabled={loadingCountries}
                  disabled={loadingCountries || !manualAddress}
              />
            </FormControl>
          </Box>

          {/* State/Province Dropdown */}
          <Box>
            <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, textAlign: "left" }}>
              {t("province")} <span style={{ color: "red" }}>*</span>
            </CustomText>
            <FormControl
              fullWidth
              size="small"
              // disabled={!selectedCountry}
              disabled={!manualAddress || !selectedCountry}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  borderRadius: "6px",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#091E4224",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: selectedCountry ? "#1976d2" : "#ccc",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: selectedCountry ? "#1976d2" : "#ccc",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  color: "#6B778C",
                  "&.Mui-focused": {
                    color: selectedCountry ? "#1976d2" : "#ccc",
                  },
                },
              }}
            >
              <SearchableDropdown
                value={selectedState}
                onChange={handleStateChange}
                options={states}
                placeholder={t("selectState")}
                // disabled={!selectedCountry}
                disabled={!manualAddress || !selectedCountry}
              />
            </FormControl>
          </Box>

          {/* City Dropdown */}
          <Box>
            <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, textAlign: "left" }}>
              {t("city")} <span style={{ color: "red" }}>*</span>
            </CustomText>
            <FormControl
              fullWidth
              size="small"
              disabled={!manualAddress || !selectedState}
              // disabled={!selectedState}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  borderRadius: "6px",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#091E4224",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: selectedState ? "#1976d2" : "#ccc",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: selectedState ? "#1976d2" : "#ccc",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  color: "#6B778C",
                  "&.Mui-focused": {
                    color: selectedState ? "#1976d2" : "#ccc",
                  },
                },
              }}
            >
              <SearchableDropdown
                value={selectedCity}
                onChange={handleCityChange}
                options={cities}
                placeholder={t("selectCity")}
                // disabled={!selectedState}
                disabled={!manualAddress || !selectedState}
              />

            </FormControl>
          </Box>
        </Box>

        {/* Footer buttons */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 4, gap: 2 }}>
          <CustomButton
            disabled={saving}
            size="medium"
            label={t("back")}
            variant="text"
            onClick={() => navigate(-1)}
            sx={{
              px: 6,
              backgroundColor: saving ? "#f0f0f0" : "white",
              color: saving ? "#999" : "black",
              "&:hover": {
                backgroundColor: saving ? "#f0f0f0" : "white",
                color: saving ? "#999" : "black",
              },
            }}
          />

          <CustomButton
            onClick={handleContinue}
            disabled={saving}
            label={
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                {saving && <CircularProgress size={18} sx={{ color: "#fff" }} />}
                {t("continue")}
              </Box>
            }
            sx={{ px: 6 }}
          />
        </Box>
      </BaseCard>

      {/* Full Map Overlay */}
  
  {showFullMap && (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "#fff",
      zIndex: 1300,
      p: 2,
    }}
  >
    <IconButton
      onClick={() => setShowFullMap(false)}
      sx={{ position: "absolute", top: 16, right: 16, zIndex: 1400 }}
    >
      <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
    </IconButton>

    {locating && <LocatingPill label={t("fetchingLocation")} zIndex={1400} />}

    {isLoaded && (
      <GoogleMap
        center={position}
        zoom={15}
        mapContainerStyle={{ width: "100%", height: "100%" }}
      >
        <Marker
          position={position}
          draggable
          onDragEnd={(e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            const coords = { lat, lng };
            updatePosition(coords);
          }}
        />

        <Circle
          center={position}
          radius={100}
          options={{
            fillColor: "#3f51b5",
            fillOpacity: 0.2,
            strokeColor: "#3f51b5",
            strokeOpacity: 0.5,
          }}
        />
      </GoogleMap>
    )}
  </Box>
)}


      {/* Permission Modal */}
      <LocationPermissionModal
        open={showPermissionModal}
        onAllow={() => {
          setShowPermissionModal(false);
          getCurrentLocation();
        }}
        onCancel={() => setShowPermissionModal(false)}
      />
    </Box>
  );
};

export default Location;





















